import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Terminal, Waves, Activity, Boxes } from 'lucide-react';
import { APPS, IMAGES } from '../data/studio';
import { Reveal, Eyebrow } from './Reveal';

const MOCK_ICONS = [Terminal, Waves, Activity, Boxes];

export default function AppsShowcase() {
  const [active, setActive] = useState(APPS[0]);

  return (
    <section id="apps" className="relative py-28 lg:py-40" data-testid="apps-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Eyebrow accent="#10B981">Apps &amp; Technology</Eyebrow>
          <h2 className="font-celestial text-3xl sm:text-4xl lg:text-5xl font-light text-slate-100 max-w-3xl leading-tight">
            Instruments for <span className="text-emerald-300">thought</span>
          </h2>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 flex flex-wrap gap-3" role="tablist">
            {APPS.map((app) => (
              <button
                key={app.id}
                role="tab"
                aria-selected={active.id === app.id}
                onClick={() => setActive(app)}
                className={`font-mono-precision text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border transition-all duration-300 ${
                  active.id === app.id
                    ? 'border-emerald-400/70 text-emerald-300 bg-emerald-400/10 shadow-[0_0_20px_rgba(16,185,129,0.2)]'
                    : 'border-white/10 text-slate-400 hover:text-slate-200 hover:border-white/25'
                }`}
                data-testid={`app-showcase-tab-${app.id}`}
              >
                {app.name}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10 grid lg:grid-cols-2 gap-8 items-stretch">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 24 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-2xl border border-white/[0.07] bg-gradient-to-b from-slate-900/70 to-slate-950/90 p-8 lg:p-10"
              data-testid="app-detail-panel"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono-precision text-[10px] uppercase tracking-[0.25em] px-3 py-1 rounded-full border" style={{ color: active.accent, borderColor: `${active.accent}55` }}>
                  {active.status}
                </span>
                <span className="font-mono-precision text-[10px] tracking-[0.2em] text-slate-600">STUDIO BUILD</span>
              </div>
              <h3 className="font-celestial text-3xl text-slate-100 mt-8">{active.name}</h3>
              <p className="text-sm mt-2" style={{ color: active.accent }}>{active.tagline}</p>
              <p className="text-sm text-slate-400 leading-relaxed mt-5">{active.description}</p>
              <div className="mt-8">
                <p className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">Capabilities</p>
                <ul className="space-y-2">
                  {active.specs.map((s) => (
                    <li key={s} className="flex items-center gap-3 text-sm text-slate-300">
                      <span className="w-1 h-1 rounded-full" style={{ background: active.accent }} />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-8 flex flex-wrap gap-2">
                {active.stack.map((t) => (
                  <span key={t} className="font-mono-precision text-[10px] tracking-[0.15em] text-slate-400 border border-white/10 rounded-full px-3 py-1">
                    {t}
                  </span>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          <Reveal delay={0.1} className="h-full">
            <div className="relative h-full min-h-[380px] rounded-2xl overflow-hidden border border-white/[0.07] group" data-testid="app-visual-panel">
              <img src={IMAGES.hud} alt="Futuristic interface" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 transition-opacity duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#02040a] via-transparent to-[#02040a]/50" />
              <AnimatePresence mode="wait">
                <motion.div
                  key={active.id}
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.04 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <div className="w-64 rounded-xl border border-white/10 bg-[#02040a]/70 backdrop-blur-xl p-5 shadow-[0_8px_50px_rgba(0,0,0,0.6)]">
                    {(() => { const MockIcon = MOCK_ICONS[APPS.findIndex(a => a.id === active.id)]; return <MockIcon size={20} style={{ color: active.accent }} strokeWidth={1.5} />; })()}
                    <div className="mt-4 space-y-2.5">
                      {[80, 55, 68].map((wd, i) => (
                        <motion.div
                          key={`w-${wd}`}
                          initial={{ width: 0 }}
                          animate={{ width: `${wd}%` }}
                          transition={{ delay: 0.2 + i * 0.15, duration: 0.7, ease: 'easeOut' }}
                          className="h-1.5 rounded-full"
                          style={{ background: `${active.accent}${i === 0 ? '' : '66'}` }}
                        />
                      ))}
                    </div>
                    <p className="mt-4 font-mono-precision text-[10px] tracking-[0.2em] text-slate-500">{active.name.toUpperCase()} · LIVE MOCK</p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
